import { useEffect } from "react";

/**
 * AUT-237 (`ui/truncation-tooltips`): a shared, throttled resize pass for every `TruncatedText`
 * instance on the page. Copies the module-level subscriber-registry shape of
 * `useStickyHeaderOffset.ts:34-71` -- one listener installed on first subscribe, one
 * `requestAnimationFrame` coalescing pass per burst -- because the keyword bank and
 * `UnifiedLandscapeTable` can render hundreds of instances at once (NFR-2), and a `ResizeObserver`
 * or `MutationObserver` per instance is a re-measure storm at that volume.
 *
 * Deliberately narrower than `useStickyHeaderOffset`: `resize` is the only trigger here. No `scroll`
 * listener, no `ResizeObserver` on `document.body`, no subtree `MutationObserver` -- those are a
 * re-measure storm at this volume.
 *
 * AUT-424 corrected the reasoning this docblock used to give for that narrowness ("a span's own
 * clipping cannot change without the window resizing"). It can, and does: dragging a table column
 * resizes the cell without touching the window, which is how the Keywords tab's Theme column lost
 * its tooltip. The trigger that closes that gap lives in `TruncatedText` itself -- it re-measures on
 * pointer/focus entry, which costs nothing at rest and runs only on the one element being entered.
 * This registry stays exactly as narrow as it was.
 */
const subscribers = new Set<() => void>();
let frameId = 0;

function notifySubscribers(): void {
  if (frameId) return;
  frameId = requestAnimationFrame(() => {
    frameId = 0;
    subscribers.forEach((cb) => cb());
  });
}

function subscribeToResize(cb: () => void): () => void {
  subscribers.add(cb);

  if (subscribers.size === 1) {
    window.addEventListener("resize", notifySubscribers);
  }

  return () => {
    subscribers.delete(cb);
    if (subscribers.size === 0) {
      window.removeEventListener("resize", notifySubscribers);
      if (frameId) {
        cancelAnimationFrame(frameId);
        frameId = 0;
      }
    }
  };
}

/**
 * Re-runs `measure` on the shared window-resize pass above. Per-instance re-measurement for prop
 * changes (`children`, `fullValue`, `lines`, `width`) is the caller's own effect -- this hook only
 * covers the trigger the caller cannot see on its own.
 */
export function useClippedText(measure: () => void): void {
  useEffect(() => subscribeToResize(measure), [measure]);
}
